// Share <-> deposit-token conversion, mirroring the on-chain program exactly.
//
// Both `redeem` and `redeem_asset` compute the payout as
//     token_amount = shares * staked / share_mint.supply
// in u128, and neither consults the vault `rate`. Displaying `shares / rate`
// instead drifts from the real payout as the swapper harvests yield, because
// harvesting lowers `staked` while `total_assets` is unchanged.
//
// All values are raw on-chain integers (1e9-scaled). BigInt is mandatory:
// shares * staked reaches ~1e22, well past Number.MAX_SAFE_INTEGER.
// The es2015 build target forbids BigInt literals, so use BigInt(n).

const ZERO = BigInt(0);

/** Deposit tokens a share balance can redeem, before withdrawal fees. */
export const computeWithdrawableRaw = (
  sharesRaw: bigint,
  stakedRaw: bigint,
  shareSupplyRaw: bigint
): bigint => {
  if (shareSupplyRaw <= ZERO || sharesRaw <= ZERO || stakedRaw <= ZERO) {
    return ZERO;
  }
  return (sharesRaw * stakedRaw) / shareSupplyRaw;
};

/**
 * Inverse of computeWithdrawableRaw: shares to burn for a target token amount.
 * Floors, so a partial withdrawal never redeems more than the user asked for.
 * Callers must still clamp the result to the user's owned shares.
 */
export const computeSharesForTokensRaw = (
  tokenAmountRaw: bigint,
  stakedRaw: bigint,
  shareSupplyRaw: bigint
): bigint => {
  if (stakedRaw <= ZERO || tokenAmountRaw <= ZERO || shareSupplyRaw <= ZERO) {
    return ZERO;
  }
  return (tokenAmountRaw * shareSupplyRaw) / stakedRaw;
};

/**
 * Withdrawal fee as a display percentage.
 *
 * `withdrawal_fee` is denominated in millionths (FEE_RATE_DENOMINATOR =
 * 1_000_000), so 100_000 is the 10% cap. It is NOT basis points.
 */
export const withdrawalFeePercent = (withdrawalFee: number): number =>
  withdrawalFee / 10_000;
